'use client'

import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useRef } from 'react'

import { MButton } from '@/_components/medante/ui/m-button'
import { IPriceListItem, ITemplate, ITemplateTimeslot } from '@/_types'
import { buildGa4CartItem, pushAddToCart, pushSelectItem, pushViewItemListOnce } from '@/_utils/analytics/datalayer'

import { CartProductType, useOrderFlow } from '../../../_context/order-flow-context'
import { useGetAmbulance } from '../../../_services/useGetAmbulance'
import { useGetAmbulanceCategories } from '../../../_services/useGetAmbulanceCategories'
import { useGetProducts } from '../../../_services/useGetProducts'
import { filterTemplatesByEmployeeSlug } from '../../../_utils/template-employee.utils'
import { AOrderLoading } from '../../order-flow/_components/a-order-loading'

import { ABannerCard } from './_components/banner/a-banner-card'
import { AProductCard } from './_components/product/a-product-card'
import { AOrderTemplateCard } from './_components/template/a-product-template-card'

import type { IAmbulanceCategory } from '@/_types'

// const DEFAULT_PRODUCT_BANNER: IProductListBanner = {
// 	title: '<p>Nenašli ste službu, ktorú hľadáte?</p>',
// 	text: '<p>Kontaktujte prosím naše Klientske centrum emailom na <a href="mailto:recepcia@medante.sk">recepcia@medante.sk</a> alebo telefonicky <a href="tel:+421220302303">+421 2 20 302 303</a></p>',
// 	isEnabled: true,
// 	uuid: 'default-contact-card',
// 	sortOrder: 9999
// }

function categoryTreeContainsAmbulanceSlug(node: IAmbulanceCategory, slug: string): boolean {
	if (node.ambulance?.slug === slug) return true
	return node.children.some(child => categoryTreeContainsAmbulanceSlug(child, slug))
}

function sectionThemeForAmbulanceSlug(roots: IAmbulanceCategory[], slug: string) {
	for (const root of roots) {
		if (categoryTreeContainsAmbulanceSlug(root, slug)) {
			return root.sectionTheme ?? null
		}
	}
	return null
}

type AStepSelectProductProps = {
	/** Slug of the ambulance selected in step 1; used to fetch its product list. */
	ambulanceSlug: string
}

/**
 * Order step 2: user picks a product (template with timeslot, or price-list item)
 * from the list belonging to the previously chosen ambulance.
 *
 * Selecting any item calls `confirmProductSelection` on the order-flow context,
 * which stores the cart and advances to step 3.
 * A persistent "contact us" banner is appended after ambulance-specific banners.
 *
 * Each card uses `orderFlowSectionTheme` from context (set in step 1). If missing (e.g. deep link),
 * the theme is derived from `useGetAmbulanceCategories` and the ambulance slug.
 *
 * @param props - Step props.
 * @param props.ambulanceSlug - Ambulance slug from step 1; drives `useGetAmbulance` / `useGetProducts`.
 */
export const AStepSelectProduct = ({ ambulanceSlug }: AStepSelectProductProps) => {
	const t = useTranslations('pages.order.choose_doctor')
	const {
		confirmProductSelection,
		orderFlowSectionTheme,
		presetEmployeeSlug,
		showAllEmployees,
		clearEmployeeFilter
	} = useOrderFlow()
	const viewItemListFiredRef = useRef(false)

	const ambulanceCategories = useGetAmbulanceCategories()

	const {
		data: ambulanceResponse,
		isLoading: isAmbulanceLoading,
		isError: isAmbulanceError
	} = useGetAmbulance(ambulanceSlug)

	const {
		data: products,
		isLoading: isProductsLoading,
		isError: isProductsError
	} = useGetProducts(ambulanceResponse?.data?.id ?? null)

	const templates = useMemo(() => products?.data?.templates ?? [], [products?.data?.templates])
	const priceListItems = useMemo(() => products?.data?.priceListItems ?? [], [products?.data?.priceListItems])
	const ambulance = ambulanceResponse?.data ?? undefined

	const { visibleTemplates, isEmployeeFilterActive } = useMemo(() => {
		if (!presetEmployeeSlug || showAllEmployees) {
			return { visibleTemplates: templates, isEmployeeFilterActive: false }
		}

		const filteredTemplates = filterTemplatesByEmployeeSlug(templates, presetEmployeeSlug)
		if (filteredTemplates.length === 0) {
			return { visibleTemplates: templates, isEmployeeFilterActive: false }
		}

		return { visibleTemplates: filteredTemplates, isEmployeeFilterActive: true }
	}, [templates, presetEmployeeSlug, showAllEmployees])

	const derivedSectionTheme = useMemo(
		() => sectionThemeForAmbulanceSlug(ambulanceCategories.data?.data ?? [], ambulanceSlug),
		[ambulanceCategories.data?.data, ambulanceSlug]
	)
	const sectionTheme = orderFlowSectionTheme ?? derivedSectionTheme

	useEffect(() => {
		if (viewItemListFiredRef.current) return
		const allItems = [...templates, ...priceListItems]
		if (allItems.length === 0 || !ambulance) return
		const itemListId = `ambulancia-${ambulanceSlug}`
		const itemListName = `Ambulancia - ${ambulance.name}`
		const ga4Items = allItems.map((item, index) => {
			const isTemplate = 'timeslots' in item
			if (isTemplate) {
				return buildGa4CartItem(
					{
						type: 'template',
						templateId: item.id,
						name: item.name,
						price: item.price ?? 0
					},
					{ item_list_id: itemListId, item_list_name: itemListName, index, item_category2: ambulance.name }
				)
			}
			return buildGa4CartItem(
				{
					type: 'price-list-item',
					itemId: item.id,
					name: item.name,
					price: item.price ?? 0,
					priceOld: (item as IPriceListItem).priceOld ?? undefined
				},
				{ item_list_id: itemListId, item_list_name: itemListName, index, item_category2: ambulance.name }
			)
		})
		const pushed = pushViewItemListOnce(itemListId, {
			item_list_id: itemListId,
			item_list_name: itemListName,
			items: ga4Items
		})
		if (pushed) viewItemListFiredRef.current = true
	}, [templates, priceListItems, ambulance, ambulanceSlug])

	// Merge ambulance-specific banners (sorted by sortOrder) with the default fallback.
	const filteredBanners = useMemo(() => {
		const ambulanceBanners = ambulanceResponse?.data?.productListBanners ?? []

		const sortedAmbulanceBanners = ambulanceBanners
			.filter(banner => banner.isEnabled)
			.sort((a, b) => a.sortOrder - b.sortOrder)

		// return [...sortedAmbulanceBanners, DEFAULT_PRODUCT_BANNER]
		return [...sortedAmbulanceBanners]
	}, [ambulanceResponse?.data?.productListBanners])

	if (isAmbulanceLoading) {
		return <AOrderLoading message='Načítavam ambulanciu…' />
	}

	if (isProductsLoading) {
		return <AOrderLoading message='Načítavam produkty…' />
	}

	if (isAmbulanceError || isProductsError) {
		return (
			<div className='box_ordering-system-hero_right flex min-w-0 max-w-full items-center justify-center overflow-x-hidden py-12'>
				<p className='text-sm opacity-60'>Ambulanciu sa nepodarilo načítať.</p>
			</div>
		)
	}

	const itemListId = ambulance ? `ambulancia-${ambulanceSlug}` : ''
	const itemListName = ambulance ? `Ambulancia - ${ambulance.name}` : ''

	const handleTemplateSelect = (template: ITemplate, timeslot: ITemplateTimeslot) => {
		const index = templates.findIndex(t => t.id === template.id)
		const listContext = {
			item_list_id: itemListId,
			item_list_name: itemListName,
			...(index >= 0 && { index }),
			item_category2: ambulance?.name,
			ambulanceSlug
		}
		const cartItem = {
			id: template.id,
			templateId: template.id,
			timeslotId: timeslot.id,
			name: template.name,
			type: CartProductType.TEMPLATE,
			price: template.price ?? 0
		}
		pushSelectItem(cartItem, listContext)
		pushAddToCart(cartItem, { item_category2: ambulance?.name, ambulanceSlug })
		confirmProductSelection([
			{
				type: CartProductType.TEMPLATE,
				data: { templateId: template.id, timeslotId: timeslot.id }
			}
		])
	}

	const handlePriceListItemSelect = (priceListItem: IPriceListItem) => {
		const index =
			templates.length + priceListItems.findIndex(i => i.id === priceListItem.id)
		const listContext = {
			item_list_id: itemListId,
			item_list_name: itemListName,
			...(index >= 0 && { index }),
			item_category2: ambulance?.name,
			ambulanceSlug
		}
		const cartItem = {
			id: priceListItem.id,
			itemId: priceListItem.id,
			name: priceListItem.name,
			type: CartProductType.PRICE_LIST_ITEM,
			price: priceListItem.price,
			priceOld: priceListItem.priceOld ?? undefined
		}
		pushSelectItem(cartItem, listContext)
		pushAddToCart(cartItem, { item_category2: ambulance?.name, ambulanceSlug })
		confirmProductSelection([
			{ type: CartProductType.PRICE_LIST_ITEM, data: { itemId: priceListItem.id } }
		])
	}

	return (
		<>
			{visibleTemplates.map(template => (
				<AOrderTemplateCard
					key={template.id}
					ambulance={ambulance}
					sectionTheme={sectionTheme}
					template={template}
					onSelect={handleTemplateSelect}
				/>
			))}

			{isEmployeeFilterActive && (
				<div className='my-8 flex justify-center'>
					<MButton
						type='button'
						variant='purple'
						icon='arrow-2-right'
						onClick={clearEmployeeFilter}
					>
						{t('showAllDoctors')}
					</MButton>
				</div>
			)}

			{priceListItems.map(item => (
				<AProductCard
					key={item.id}
					sectionTheme={sectionTheme}
					product={item}
					onSelect={handlePriceListItemSelect}
				/>
			))}

			{filteredBanners.map(banner => (
				<ABannerCard key={banner.uuid} sectionTheme={sectionTheme} banner={banner} />
			))}
		</>
	)
}
